import { forwardRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { LucideIcon, Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-normal transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-[#468AD4] text-white hover:bg-[#3672b3] active:bg-[#1B3050] border border-[#468AD4] focus-visible:ring-[#468AD4]',
        secondary: 'bg-[#865ce2] text-white hover:bg-[#7650c7] active:bg-[#6644ad] border border-[#865ce2] focus-visible:ring-[#865ce2]',
        success: 'bg-[#2DC47A] text-white hover:bg-[#24a366] active:bg-[#1c8352] border border-[#2DC47A] focus-visible:ring-[#2DC47A]',
        danger: 'bg-[#ed5e5e] text-white hover:bg-[#c95050] active:bg-[#a54242] border border-[#ed5e5e] focus-visible:ring-[#ed5e5e]',
        warning: 'bg-[#e8bc52] text-white hover:bg-[#c5a046] active:bg-[#a2843a] border border-[#e8bc52] focus-visible:ring-[#e8bc52]',
        info: 'bg-[#50c3e6] text-white hover:bg-[#44a6c4] active:bg-[#3889a2] border border-[#50c3e6] focus-visible:ring-[#50c3e6]',
        light: 'bg-[#F4F6F9] text-[#212529] hover:bg-[#e9ebec] active:bg-[#ced4da] border border-[#F4F6F9] focus-visible:ring-[#F4F6F9]',
        dark: 'bg-[#1B3050] text-white hover:bg-[#0D1E33] active:bg-[#06101c] border border-[#1B3050] focus-visible:ring-[#1B3050]',
        // Equipment hotfix (PR #886) added dark-mode classes for both
        // ghost and outline. The Customer-module quickfix branch added
        // dark-mode for outline only with a slightly different token set
        // (#1E1E1E / #F4F6F9). Develop's tokens (white/15 border,
        // #1A1A1A bg, #C2DAF2 text, white/5 hover, white/10 active) are
        // the dominant pattern shared with Select.tsx, so take develop's
        // version — it covers both variants and stays consistent.
        ghost: 'text-[#495057] hover:bg-[#F4F6F9] active:bg-[#eff2f7] border border-transparent dark:text-[#C2DAF2] dark:hover:bg-white/5 dark:active:bg-white/10',
        outline: 'border border-[#ced4da] bg-white text-[#495057] hover:bg-[#F4F6F9] active:bg-[#eff2f7] focus-visible:ring-[#ced4da] dark:border-white/15 dark:bg-[#1A1A1A] dark:text-[#C2DAF2] dark:hover:bg-white/5 dark:active:bg-white/10',
        // Customer-module quickfix follow-up: soft-* variants previously had
        // no dark-mode classes, so e.g. `soft-primary` rendered as a near-white
        // light-blue blob on the dark form (very visible mismatch against
        // primary/outline buttons that now have proper dark variants). Each
        // soft-* variant gets a tinted-overlay bg + brand-tinted text, mirroring
        // the SearchableSelect selected-option palette (`rgba(brand,0.2)`/lighter
        // text). Hover keeps the existing "fill with brand color, white text"
        // behavior so the button still feels tactile in both themes.
        'soft-primary': 'bg-[#e0ecf8] text-[#468AD4] hover:bg-[#468AD4] hover:text-white border border-transparent dark:bg-[rgba(70,138,212,0.18)] dark:text-[#7BB0E8] dark:hover:bg-[#468AD4] dark:hover:text-white',
        'soft-secondary': 'bg-[#ede7fb] text-[#865ce2] hover:bg-[#865ce2] hover:text-white border border-transparent dark:bg-[rgba(134,92,226,0.18)] dark:text-[#B49BEE] dark:hover:bg-[#865ce2] dark:hover:text-white',
        'soft-success': 'bg-[#d4f5e5] text-[#2DC47A] hover:bg-[#2DC47A] hover:text-white border border-transparent dark:bg-[rgba(45,196,122,0.18)] dark:text-[#6FDFA6] dark:hover:bg-[#2DC47A] dark:hover:text-white',
        'soft-info': 'bg-[#e5f6fb] text-[#50c3e6] hover:bg-[#50c3e6] hover:text-white border border-transparent dark:bg-[rgba(80,195,230,0.18)] dark:text-[#8FD9EE] dark:hover:bg-[#50c3e6] dark:hover:text-white',
        'soft-warning': 'bg-[#fcf5e5] text-[#e8bc52] hover:bg-[#e8bc52] hover:text-white border border-transparent dark:bg-[rgba(232,188,82,0.18)] dark:text-[#F0D182] dark:hover:bg-[#e8bc52] dark:hover:text-white',
        'soft-danger': 'bg-[#fce7e7] text-[#ed5e5e] hover:bg-[#ed5e5e] hover:text-white border border-transparent dark:bg-[rgba(237,94,94,0.18)] dark:text-[#F18888] dark:hover:bg-[#ed5e5e] dark:hover:text-white',
      },
      size: {
        sm: 'px-2 py-1 text-[11.55px] rounded-[3.2px]',
        md: 'px-[14.4px] py-2 text-[13.2px] rounded-[4px]',
        lg: 'px-[19.2px] py-[10px] text-[16.5px] rounded-[4.8px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

interface ButtonProps extends VariantProps<typeof buttonVariants> {
  children: ReactNode;
  icon?: LucideIcon;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  className?: string;
  onClick?: () => void;
  // WAYP-509: forward `data-testid` + `aria-label` so pages using <Button>
  // with `data-testid="..."` expose a stable selector to Playwright.
  // Without these, consumer `data-testid` props were silently dropped
  // because the component destructured only the named props and passed
  // nothing else to the underlying <button>.
  'data-testid'?: string;
  'aria-label'?: string;
  // ATL-000964: forward `title` so a truncated button label can expose its
  // full text as a native tooltip (hover/focus) without widening the button.
  title?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      children,
      icon: Icon,
      loading = false,
      disabled = false,
      type = 'button',
      form,
      className,
      onClick,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      title,
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        form={form}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        onClick={onClick}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        title={title}
        className={clsx(buttonVariants({ variant, size }), className)}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : Icon ? (
          <Icon className="h-4 w-4" />
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
